/*
 *                     NOTICE
 *            COPYRIGHT 2022 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { ReactUtils } from "abreact/incubator/ui/utils/ReactUtils";
import { TypeUtils } from "abweb/utils/TypeUtils";
/* Special symbol that should never be used outside this file. Allows for AbEnrichable to make assumptions about
 * what data shapes are possible. */
const AB_ENRICHMENT = Symbol("AbEnrichment");

/* DO NOT CREATE THIS BY HAND. Use AbEnriched.create. */
export let AbEnriched;
(function (_AbEnriched) {
  const create = _AbEnriched.create = (item, enrichment) => {
    if (AbEnriched.isEnriched(item)) {
      throw new Error("Can't enrich an enriched item.");
    }
    return {
      item: item,
      [AB_ENRICHMENT]: true,
      enrichment: enrichment
    };
  };
  const createMaybe = _AbEnriched.createMaybe = (item, enrichment) => {
    if (enrichment === undefined) return item;
    return AbEnriched.create(item, enrichment);
  };
  const getItem = _AbEnriched.getItem = item => {
    if (isEnriched(item)) {
      return item.item;
    }
    return item;
  };
  const getItems = _AbEnriched.getItems = items => {
    return items.map(item => AbEnriched.getItem(item));
  };
  const getEnrichment = _AbEnriched.getEnrichment = item => {
    if (isEnriched(item)) {
      return item.enrichment;
    }
    return undefined;
  };
  const extract = _AbEnriched.extract = item => {
    if (isEnriched(item)) {
      return {
        item: AbEnriched.getItem(item),
        enrichment: AbEnriched.getEnrichment(item)
      };
    }
    return {
      item: item,
      enrichment: undefined
    };
  };
  const isEnriched = _AbEnriched.isEnriched = item => {
    return typeof item === "object" && item !== null && AB_ENRICHMENT in item;
  };
  const addClassname = _AbEnriched.addClassname = (maybeEnrichedItem, className) => {
    const {
      item,
      enrichment
    } = AbEnriched.extract(maybeEnrichedItem);
    if (!enrichment) return AbEnriched.create(item, {
      className: className
    });
    return AbEnriched.create(item, {
      ...enrichment,
      className: ReactUtils.classNamesString([enrichment.className, className])
    });
  };
  const markDisabled = _AbEnriched.markDisabled = maybeEnrichedItem => {
    const {
      item,
      enrichment
    } = AbEnriched.extract(maybeEnrichedItem);
    if (!enrichment) return AbEnriched.create(item, {
      disabled: true
    });
    return AbEnriched.create(item, {
      ...enrichment,
      disabled: true
    });
  };

  /**
   * Convenience method for wrapping a KeyFunction that works on type T to work on AbMaybeEnriched<T, E>
   */

  const createMaybeEnrichedKeyFunction = _AbEnriched.createMaybeEnrichedKeyFunction = keyFunction => {
    return maybeEnriched => TypeUtils.itemToKey(AbEnriched.getItem(maybeEnriched), keyFunction);
  };
  const isSelectable = _AbEnriched.isSelectable = enrichment => {
    if (enrichment?.disabled || enrichment?.displayOnly) {
      return false;
    }
    return true;
  };
})(AbEnriched || (AbEnriched = {}));
//# sourceMappingURL=AbEnriched.js.map
